/**
 * SessionContext — who the SERVER says you are, and what it will let you do.
 *
 * MSAL answers a different question than this does. MSAL tells us a person signed in to the tenant;
 * it says nothing about whether they have a Stride role. Someone can hold a perfectly valid token and
 * still be refused everything, so the UI needs the server's answer (`GET /api/me`) rather than
 * inferring capability from the token it happens to hold.
 *
 * Three states worth naming, because collapsing them produces the two worst failure modes:
 *   · `no-role`      — signed in, no Stride role. MUST NOT be treated as "not signed in": the SPA
 *                      answers unauthenticated by redirecting to sign-in, so treating this as
 *                      unauthenticated is an endless loop through a login that always succeeds and
 *                      never grants anything.
 *   · `unavailable`  — the SERVER can't authenticate anyone (auth unconfigured, 503). Also not a
 *                      credential problem, also must not redirect. Someone has to fix the server.
 *   · `ready`        — we have a principal and its capabilities.
 *
 * Capabilities come from the server (`can.read/deploy/administer`) rather than being re-derived from
 * the role name here. One ladder, on the server; the UI renders what it is told, so the two cannot
 * drift into disagreeing about what an operator may do.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { WhoAmI } from '@stride/shared'
import { apiGet, ApiError } from '../lib/apiClient'

export type SessionState = 'loading' | 'ready' | 'no-role' | 'unavailable'

export interface Session {
  state:     SessionState
  me:        WhoAmI | null
  /** Server-side reason when state is 'no-role' or 'unavailable' — shown verbatim; it's written for a human. */
  reason:    string | null
  /** Re-ask the server. Used after an admin grants a role, so the person doesn't have to reload. */
  refresh:   () => void
  /** Convenience mirrors of `me.can`, false until known. Never guess upward while loading. */
  canRead:   boolean
  canDeploy: boolean
  canAdmin:  boolean
  /** True when the API is running with auth bypassed (local only) — surfaced so nobody mistakes a
   *  local session for a secured one. */
  devBypass: boolean
}

const Ctx = createContext<Session>({
  state: 'loading', me: null, reason: null, refresh: () => {},
  canRead: false, canDeploy: false, canAdmin: false, devBypass: false,
})

/**
 * True in mock-auth mode — the SPA skips MSAL, and the API is expected to be on its dev bypass.
 *
 * Read at call time rather than captured in a module constant: a module-level constant is evaluated
 * once at import, which makes the two modes untestable in the same run.
 */
function mockAuth(): boolean {
  return import.meta.env.VITE_MOCK_AUTH === 'true'
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<SessionState>('loading')
  const [me, setMe] = useState<WhoAmI | null>(null)
  const [reason, setReason] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => { setTick((n) => n + 1) }, [])

  /**
   * Whether a session has ever resolved. A ref, not state, because it must not itself trigger the effect.
   */
  const established = useRef(false)

  useEffect(() => {
    let cancelled = false
    /**
     * Blank the session only on the FIRST read — NOT on a refresh.
     *
     * A refresh re-reads a session that already exists. Dropping to `loading` in that case unmounts the
     * entire authenticated tree, which destroys the component that asked for the refresh along with
     * whatever it was about to show.
     *
     * Found by a browser test on the Permissions screen: saving a group correctly refreshes the session
     * (editing a group you belong to changes your own access), and the confirmation message vanished with
     * the remount. It would also have flashed the whole app to a spinner on every such save.
     *
     * A revoking refresh still takes effect — the handlers below set `no-role` or `unavailable` on the
     * result. The only thing that changes is that the UI keeps showing the last known-good session while
     * the re-read is in flight, rather than showing nothing.
     */
    if (!established.current) setState('loading')
    apiGet<WhoAmI>('/api/me')
      .then((who) => {
        if (cancelled) return
        setMe(who); setReason(null); setState('ready')
        established.current = true
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const status = err instanceof ApiError ? err.status : 0
        const message = err instanceof Error ? err.message : String(err)
        // 403 → authenticated, unassigned. 503 → the server can't authenticate anyone. Neither is a
        // credential problem, and neither may trigger a sign-in redirect.
        if (status === 403) { setReason(message); setState('no-role'); return }
        setReason(message); setState('unavailable')
      })
    return () => { cancelled = true }
  }, [tick])

  const can = me?.can
  const value: Session = {
    state, me, reason, refresh,
    canRead:   can?.read === true,
    canDeploy: can?.deploy === true,
    canAdmin:  can?.administer === true,
    // In mock-auth mode the SPA never talks to a real Entra tenant, so treat it as a bypass session
    // even if the API somehow answers otherwise — the banner should be showing.
    devBypass: me?.devBypass === true || mockAuth(),
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useSession(): Session {
  return useContext(Ctx)
}
